import { useState, useEffect, useCallback } from 'react';
import { Loader2, ArrowUpRight, RotateCcw, Layers } from 'lucide-react';
import { api } from '../../lib/spizirnaUi';
import { POPIS_POLE, popisHodnoty } from './NastaveniPridavku';
import { SECTION_COLOR } from './receptUtils';

const VLASTNI = '#3B82F6';

/**
 * Třetí sloupec detailu kategorie — koho se kategorie týká.
 *
 * Počty produktů a velikostí, a hlavně seznam míst, která mají vlastní
 * nastavení ingrediencí. To, co by jinak ukázal až dialog přepsání, je tak
 * vidět dopředu. Z každého místa jde otevřít jeho detail, nebo ho vrátit
 * na hodnoty kategorie.
 */
export default function KategorieDopad({
    node, pocty, isDark, cardStyle, mutedColor, secondaryColor,
    obnovit = 0, onOtevrit, onVraceno, jenIngredience = true,
}) {
    const [mista, setMista] = useState(null);
    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    const nacist = useCallback(() => {
        if (!jenIngredience) { setMista([]); return; }
        api(`/api/recepty/pridavky-nastaveni/kategorie/${node.id}/vlastni-pod`).then(setMista).catch(() => setMista([]));
    }, [node.id, jenIngredience]);

    useEffect(() => { setMista(null); nacist(); }, [nacist]);
    useEffect(() => { if (obnovit) nacist(); }, [obnovit]); // eslint-disable-line react-hooks/exhaustive-deps

    const vratit = async (m) => {
        if (!confirm(`Vrátit ${m.nazev} na nastavení kategorie ${node.nazev}?`)) return;
        const zmeny = m.hodnoty.map(h => ({ skupina_id: h.skupina_id, pridavek_id: h.pridavek_id, pole: h.pole, hodnota: null }));
        try {
            // U podkategorie by vrácení mohlo narazit na místa pod ní — ta
            // si vlastní hodnoty nechají, proto rovnou „zachovat".
            await api(`/api/recepty/pridavky-nastaveni/${m.typ}/${m.id}`, 'PUT', { zmeny, ...(m.typ === 'kategorie' ? { rezim: 'zachovat' } : {}) });
            nacist();
            onVraceno?.();
        } catch (e) { alert(e?.error || e?.message || 'Nepodařilo se vrátit'); }
    };

    const nadpis = (text) => (
        <div className="flex items-center gap-1.5 mb-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: secondaryColor }}>{text}</span>
        </div>
    );
    const radek = (popis, hodnota) => (
        <div className="flex items-center justify-between text-[12px] py-1">
            <span style={{ color: mutedColor }}>{popis}</span>
            <span className="font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{hodnota}</span>
        </div>
    );

    return (
        <>
            <div className="rounded-xl p-3 flex-shrink-0" style={cardStyle}>
                <div className="flex items-center gap-1.5 mb-2">
                    <Layers size={13} style={{ color: SECTION_COLOR }} />
                    <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: secondaryColor }}>Dopad</span>
                </div>
                {radek('Produktů', pocty.produktu)}
                {pocty.velikosti !== pocty.produktu && radek('Velikostí', pocty.velikosti)}
                {radek('Podkategorií', pocty.podkategorii)}
                {mista && jenIngredience && radek('Vlastní nastavení', mista.length)}
            </div>

            {jenIngredience && (
                <div className="rounded-xl p-3 flex-shrink-0" style={cardStyle}>
                    {nadpis(`Vlastní ingredience pod ${node.nazev}`)}
                    {mista === null ? (
                        <div className="flex justify-center py-3"><Loader2 size={16} className="animate-spin" style={{ color: mutedColor }} /></div>
                    ) : mista.length === 0 ? (
                        <p className="text-[12px]" style={{ color: mutedColor }}>Všechno pod kategorií dědí její nastavení.</p>
                    ) : (
                        <div className="space-y-2">
                            {mista.map(m => (
                                <div key={`${m.typ}:${m.id}`} className="rounded-lg px-2.5 py-2"
                                    style={{ background: `${VLASTNI}12`, border: `1px solid ${VLASTNI}40` }}>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-[12px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                                            {m.nazev}
                                            {m.typ === 'kategorie' && <span className="ml-1 font-normal" style={{ color: mutedColor }}>· podkategorie</span>}
                                        </span>
                                        <button type="button" onClick={() => onOtevrit?.(m)} title="Otevřít detail"
                                            className="flex items-center gap-0.5 text-[11px] font-semibold flex-shrink-0"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: VLASTNI }}>
                                            Otevřít <ArrowUpRight size={11} />
                                        </button>
                                    </div>
                                    <ul className="mt-1 space-y-0.5">
                                        {m.hodnoty.slice(0, 4).map((h, i) => (
                                            <li key={i} className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                                                {h.pridavek || h.skupina}: {POPIS_POLE[h.pole]} {popisHodnoty(h.pole, h.hodnota)}
                                            </li>
                                        ))}
                                        {m.hodnoty.length > 4 && <li className="text-[11px]" style={{ color: mutedColor }}>a {m.hodnoty.length - 4} dalších</li>}
                                    </ul>
                                    <button type="button" onClick={() => vratit(m)}
                                        className="flex items-center gap-1 text-[11px] mt-1.5"
                                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: mutedColor, textDecoration: 'underline' }}>
                                        <RotateCcw size={10} /> Vrátit na {node.nazev}
                                    </button>
                                </div>
                            ))}
                            <p className="text-[11px] pt-1" style={{ color: mutedColor, borderTop: `1px solid ${border}` }}>
                                Změna na kategorii se těchto míst nedotkne, dokud je nepřepíšeš.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
